import React, { useContext, useState, useEffect } from "react";
import { Box, Button, IconButton, Typography, Divider, useTheme, styled } from "@mui/material";
import ImageComponent from "ui/components/shared/ImageComponent";
import LinearProgress from "@mui/material/LinearProgress";
import { Delete, Search } from '@mui/icons-material';
import AddSkillIcon from 'assets/icons/message-circle.png'
import AddSkillDisabledIcon from 'assets/icons/message-circle-disabled.png'
import { TextSizeContext } from "data/index";
import ConfirmationModal from "ui/components/shared/ConfirmationModal";
import { ParagraphInterface, SkillboxCourseInterface } from "entities/interfaces";
import { POST } from "api/index";
import { useNavigate } from "react-router-dom";


const ProfileImageContainer = styled(Box)({
  width: '3.75rem',
  height: '3.75rem',
  borderRadius: '50%',
  overflow: 'hidden',
  flexShrink: 0,
  backgroundColor: '#e5e7eb',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '2px solid rgba(161, 183, 165, 0.15)',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s ease',
  
  '&:hover': {
    transform: 'scale(1.05)',
    borderColor: 'rgba(161, 183, 165, 0.3)',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.12)'
  },
  
  '@media (max-width: 390px)': {
    width: '3.5rem',
    height: '3.5rem'
  },
  
  '@media (max-width: 375px)': {
    width: '3.25rem',
    height: '3.25rem'
  },
  
  '@media (max-width: 320px)': {
    width: '3rem',
    height: '3rem',
    border: '1.5px solid rgba(161, 183, 165, 0.15)'
  },
  
  '@media (max-width: 280px)': {
    width: '2.75rem',
    height: '2.75rem',
    border: '1px solid rgba(161, 183, 165, 0.15)'
  }
});

const DeleteButton = styled(Box)({
  position: 'absolute',
  right: 0,
  top: 0,
  bottom: 0,
  width: '80px',
  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.2)'
});




const SkillboxRowMobile = ({
  skill,
  addUserCourse,
  deleteUserCourse,
  setChapterNote,
  getChapterNote,
  getChapterHistory,
  getChapterMaterial,
}: {
  skill: SkillboxCourseInterface,
  addUserCourse: (courseId: number) => Promise<number>,
  deleteUserCourse: (courseId: number) => Promise<number>
  setChapterNote: (chapterId: number, note: string) => Promise<void>;
  getChapterNote: (chapterId: number) => Promise<string>
  getChapterHistory: (chapterId: number) => Promise<ParagraphInterface[]>
  getChapterMaterial: (chapterId: number) => void
}) => {
  
  const { state: textSize } = useContext(TextSizeContext);
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate();
  const token = window.localStorage.getItem("currentUserToken");

  const [completedChapters, setCompletedChapters] = useState<number[]>([]);
  const [progress, setProgress] = useState(0);
  const [totalLearnings, setTotalLearnings] = useState(0);
  const [loading, setLoading] = useState(false);

  // Fortschritt und Learnings laden (wie Desktop, aber robust auf skill geprüft)
useEffect(() => {
  const fetchProgress = async () => {
    if (!skill || !skill.id || !Array.isArray(skill.chapters)) return;
    setLoading(true);
    try {
      // Abgeschlossene Kapitel laden
      const body = new URLSearchParams();
      if (token) body.append("userToken", token);

      const response = await POST("getCompletedChapters", body);
      const completed = response?.completed_chapters?.map(
        (chapter: { chapter_id: number }) => chapter.chapter_id
      ) || [];
      setCompletedChapters(completed);

      // Fortschritt setzen (kommt ggf. direkt aus skill)
      setProgress(skill.progress ?? 0);

      // Freigeschaltete Kapitel filtern
      const unlockedChapters = skill.chapters.filter(chapter =>
        completed.includes(Number(chapter.id))
      );
      const unlockedChapterIds = unlockedChapters.map(c => c.id);

      let total = 0;
      if (unlockedChapterIds.length > 0) {
        const body = new URLSearchParams();
        unlockedChapterIds.forEach(id => body.append("chapter_ids[]", id.toString()));
        const learningResponse = await POST("getLearningCountsForChapters", body);
        const counts = learningResponse?.chapterLearningCounts || {};
        total = Object.values(counts).reduce((sum: number, count: any) => sum + Number(count), 0);
      }

      setTotalLearnings(total);
    } catch (error) {
      setCompletedChapters([]);
      setProgress(0);
      setTotalLearnings(0);
    }
    setLoading(false);
  };
  fetchProgress();
}, [skill, token]);


  if (!skill || !skill.id || !Array.isArray(skill.chapters)) return null;

  return (
    <Box sx={{ width: "100%"}}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          py: 1,
          px: 1.5,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          opacity: loading ? 0.6 : 1,
        }}
      >
        {/* Thumbnail */}
        <Box sx={{ flexShrink: 0, mr: 1 }}>
                    <ProfileImageContainer>
                      <ImageComponent 
                        src={skill.thumbnail} 
                        alt={skill.title}
                        width="100%"
                        height="100%"
                        objectFit
                      />
                    </ProfileImageContainer>
        </Box>

        {/* Mittelteil: Titel, Kapitel, Progressbar */}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography
            fontWeight={500}
            fontSize="1rem"
            sx={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}
          >
            {skill.title}
          </Typography>
                                <Button
            variant="contained"
            color="success"
            size="small"
            sx={{
              px: 1.5, py: 0.2, borderRadius: "1rem", minWidth: "unset",
              fontSize: "0.85rem", fontWeight: 500, boxShadow: "none", textTransform: "none"
            }}
            disabled={loading}
            onClick={() =>
              navigate(`/skillbox/${skill.id}`, { state: { courseTitle: skill.title } })
            }
          >
            {totalLearnings} Learnings
          </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" fontSize="0.9rem">
            {skill.chapters.length} Kapitel
          </Typography>
          {/* Fortschrittsleiste */}
          
          <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 5,
                flexGrow: 1,
                backgroundColor: "rgba(0,0,0,0.08)",
                mr: 1,
              }}
            />
            <Typography variant="caption" minWidth="2.5rem" textAlign="right">
              {progress.toFixed(0)}%
            </Typography>
            

          </Box>
        </Box>
      </Box>

      {deleteConfirmationModal && (
        <ConfirmationModal
          closeModal={() => setDeleteConfirmationModal(false)}
          confirmColor="error"
          handleConfirm={async () => {
            await deleteUserCourse(skill.id);
            setDeleteConfirmationModal(false);
          }}
          title="Kurs löschen"
          description={`Sind Sie sicher, dass Sie ${skill.title} löschen wollen?`}
        />
      )}
    </Box>
  );
};

export default SkillboxRowMobile;
